import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Empty, Image, Input, Spin } from 'antd';
import Tooltip from '../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { getMediaBank } from '../../requests/documentDatas';
import {
  MEDIA_TYPES,
  mediaBankView,
  normalizeMediaBank,
  mediaThumbnailUrl,
} from '../../shared/media/mediaBank';
import { OVERLAY_Z_INDEX_CEILING, getPrimaryDark } from '../../constants/constants';

// Vignette de la banque : le clic sélectionne le média (comportement existant),
// donc l'aperçu antd ne peut pas être posé directement sur la vignette (comme
// dans FormImagePredefinedValues) — rc-image appelle aussi le onClick qu'on lui
// passe après avoir ouvert l'aperçu, ce qui sélectionnerait l'image à chaque
// prévisualisation. L'œil est ici une icône dédiée, affichée seulement au
// survol, dont le clic est stoppé avant d'atteindre le bouton de sélection ;
// l'aperçu proprement dit est une Image antd contrôlée, rendue cachée, qui ne
// sert qu'à sa fenêtre modale.
// Prop de contrôle : `visible`/`onVisibleChange`, pas `open`/`onOpenChange`
// (Modal/Popover) — rc-image n'a jamais renommé cette paire lors du passage à
// antd 5, seul le nom qui a piégé le premier essai.
const BankMediaThumbnail = ({ media, onSelectMedia }) => {
  const thumbnail = mediaThumbnailUrl(media);
  const [isHovered, setIsHovered] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  return (
    <div
      /* minWidth 0 : sans lui la colonne de la grille s'élargit à la longueur
         du nom de fichier (min-width auto par défaut sur un item de grille) et
         le nom déborde du panneau au lieu d'être coupé. */
      style={{ position: 'relative', minWidth: 0 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tooltip title={media.filename} placement="top" mouseEnterDelay={0.3}>
        <button
          type="button"
          data-outside-ignore="true"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelectMedia(media);
          }}
          style={thumbButtonStyle}
        >
          <img src={thumbnail} alt={media.filename} loading="lazy" style={thumbImgStyle} />
        </button>
      </Tooltip>
      {isHovered && (
        <FontAwesomeIcon
          icon={icon({ name: 'eye', style: 'solid' })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewVisible(true);
          }}
          style={eyeIconStyle}
        />
      )}
      {/* Deux photos de chantier se ressemblent : la vignette seule ne dit pas
          laquelle on pose. Le nom est tronqué sur une ligne (la colonne fait la
          moitié du panneau), le nom entier reste dans l'infobulle. */}
      <div style={filenameStyle} title={media.filename}>
        {media.filename || 'Sans nom'}
      </div>
      <Image
        src={media.variants.max?.url || media.source}
        style={{ display: 'none' }}
        // zIndex : sans lui l'aperçu prend le défaut (bas) d'antd et passe sous
        // la barre flottante d'une box sélectionnée ailleurs sur le canvas (qui
        // peut monter jusqu'à 99999 dans cette app).
        preview={{
          visible: previewVisible,
          onVisibleChange: setPreviewVisible,
          zIndex: OVERLAY_Z_INDEX_CEILING,
        }}
      />
    </div>
  );
};

/* Contexte PDF : un GIF ou une vidéo n'y a jamais eu sa place, et son palier
   `pdf` est nul de toute façon — il n'y a rien à insérer. */
const PDF_TYPES = [MEDIA_TYPES.image];

// Cache module (clé = type:id du document) : la banque ne change pas pendant
// l'édition, on évite de refetch à chaque sélection d'une box image.
const bankCache = new Map();

const MediaBankPicker = ({ onSelectMedia }) => {
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  // Bumpé après chaque upload éditeur -> intégré à la clé de cache pour forcer
  // un refetch (la nouvelle image est rangée dans le dossier auto du document).
  const mediaBankVersion = useStore(({ mediaBankVersion }) => mediaBankVersion);
  const accentColor = getPrimaryDark(currentDocument?.type === 'template');
  const cacheKey = `${currentDocument?.type}:${currentDocument?.id}:${mediaBankVersion}`;

  const [folders, setFolders] = useState(() => bankCache.get(cacheKey) ?? null);
  const [loading, setLoading] = useState(!bankCache.has(cacheKey));
  const [query, setQuery] = useState('');
  const [openFolders, setOpenFolders] = useState([]);

  useEffect(() => {
    if (bankCache.has(cacheKey)) {
      setFolders(bankCache.get(cacheKey));
      setLoading(false);
      return;
    }
    if (!currentDocument?.id) {
      setFolders([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMediaBank(currentDocument).then((data) => {
      if (cancelled) return;
      // Contexte PDF : un GIF ou une vidéo n'y a jamais eu sa place, et son
      // palier `pdf` est nul de toute façon — il n'y a rien à insérer.
      const list = normalizeMediaBank(data)
        .map((folder) => ({
          ...folder,
          media: folder.media.filter(
            (media) => !media.webOnly && media.type === MEDIA_TYPES.image,
          ),
        }))
        .filter((folder) => folder.media.length > 0);
      bankCache.set(cacheKey, list);
      setFolders(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, currentDocument]);

  // Une banque d'entreprise peut porter des centaines de fichiers : la
  // recherche est le seul moyen d'en retrouver un sans dérouler chaque dossier.
  // Même logique que le picker du web-editor, mise en commun dans `shared`.
  const { visible } = useMemo(
    () => mediaBankView(folders ?? [], { query, types: PDF_TYPES }),
    [folders, query],
  );

  /* Chercher pour ne rien voir n'aurait aucun sens : pendant une recherche les
     dossiers qui répondent sont ouverts, et on revient replié en l'effaçant.
     `matchedKeys` est une chaîne pour que l'effet ne reboucle pas sur un
     tableau recréé à chaque rendu. */
  const matchedKeys = visible.map((folder) => String(folder.id)).join(',');
  useEffect(() => {
    setOpenFolders(query && matchedKeys ? matchedKeys.split(',') : []);
  }, [query, matchedKeys]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 12 }}>
        <Spin size="small" />
      </div>
    );
  }

  if ((folders ?? []).length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no-bank-images')} />;
  }

  const items = visible.map((folder) => ({
    key: String(folder.id),
    label: (
      <span style={{ fontSize: 12, fontWeight: 600, color: accentColor }}>
        {folder.name}
        <span style={{ color: 'rgba(11,34,57,0.45)', marginLeft: 6, fontWeight: 400 }}>
          ({folder.media.length})
        </span>
      </span>
    ),
    children: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        {folder.media.map((media) => (
          <BankMediaThumbnail key={media.id} media={media} onSelectMedia={onSelectMedia} />
        ))}
      </div>
    ),
  }));

  // Cliquer sur le header d'un dossier (Collapse) déselectionnait la box : le
  // clic bubble vers un ancêtre qui déselectionne (les vignettes, elles,
  // stoppent la propagation -> OK). On reproduit la même garde sur le wrapper :
  // stopPropagation au clic + data-outside-ignore/preventDefault au mousedown
  // (cf. useOutsideAlerter). Le toggle du Collapse s'exécute avant de remonter.
  return (
    <div
      data-outside-ignore="true"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Le mousedown du wrapper annule le focus par défaut (garde
          anti-déselection) : le champ doit couper la propagation avant, sinon
          on ne peut pas cliquer dedans pour taper. */}
      <Input
        size="small"
        allowClear
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder={t('search-media')}
        prefix={<FontAwesomeIcon icon={icon({ name: 'magnifying-glass', style: 'solid' })} style={{ opacity: 0.45 }} />}
        style={{ marginBottom: 8 }}
      />

      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no-result')} />
      ) : (
        /* Aucun dossier ouvert au départ : la banque s'ouvre sous la zone de
           dépôt, et un dossier déplié d'office poussait le reste du panel hors
           de vue. */
        <Collapse
          ghost
          size="small"
          items={items}
          activeKey={openFolders}
          onChange={setOpenFolders}
        />
      )}
    </div>
  );
};

const thumbButtonStyle = {
  // width 100% : le bouton n'est plus lui-même l'item de la grille (cf.
  // BankMediaThumbnail, qui insère un div englobant pour l'icône d'aperçu au
  // survol), il ne bénéficie donc plus de l'étirement automatique de la grille.
  width: '100%',
  padding: 0,
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  cursor: 'pointer',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const filenameStyle = {
  fontSize: 10,
  color: 'rgba(11,34,57,0.55)',
  marginTop: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const thumbImgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' };

const eyeIconStyle = {
  position: 'absolute',
  top: 4,
  right: 4,
  padding: 4,
  cursor: 'pointer',
  color: 'white',
  background: 'rgba(11,34,57,0.55)',
  borderRadius: '50%',
  fontSize: 11,
};

export default MediaBankPicker;
